import type { ConversationMessage } from '@quromedical/communication/server';
import { last } from '@quromedical/utils';

import { type AssertRule, compose, resolveConstant } from '../composer';

type CommunicationParams = {
  patientConversations: ConversationMessage[];
};

type AssertWhatsappBusinessRule = AssertRule<CommunicationParams>;

const assertCanSendWhatsappMessage: AssertWhatsappBusinessRule = ({
  patientConversations,
}) => {
  if (patientConversations.length < 1) {
    return {
      type: 'error',
      messages: ['Patient communication is required'],
    };
  }

  //For now the assumption here is that all messages are whatsapp messages, thus the business rule is to check if the last message was sent within 24 hours
  const communicationsFromPatient = patientConversations.filter(
    (comm) => comm.sender !== 'system',
  );

  if (communicationsFromPatient.length < 1) {
    return {
      type: 'error',
      messages: [
        'There is no whats communication that was initiated by the patient',
      ],
    };
  }

  const latestPatientCommunication = last(
    communicationsFromPatient.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    ),
  );

  if (!latestPatientCommunication?.createdAt) {
    return {
      type: 'error',
      messages: [
        'The Whatsapp communication from the patient does not have a sent date',
      ],
    };
  }

  const sentTime = new Date(latestPatientCommunication.createdAt).getTime();
  const whatsappWindow = sentTime + 24 * 60 * 60 * 1000;

  if (whatsappWindow < Date.now()) {
    return {
      type: 'error',
      messages: [
        'The latest Whatsapp communication from the patient was more than a 24 hours ago',
      ],
    };
  }

  return undefined;
};

// https://help.twilio.com/articles/360017773294-Rules-and-Best-Practices-for-WhatsApp-Messaging-on-Twilio
const defaultWhatsappBusinessRules: AssertWhatsappBusinessRule[] = [
  assertCanSendWhatsappMessage,
];

/**
 * a function that takes in a set of parameters and based on that determines if a consumer can do a pathology request for the patient
 */
export const validateCanSendWhatsappMessage = compose({
  getRuleName: () => 'whatsapp-business-rule',
  // assertions: encounterServiceRules,
  defaultAssertions: defaultWhatsappBusinessRules,
  defaultResolver: resolveConstant<true>(true),
});
